/**
 * DataGridPasteDialog
 *
 * AlertDialog showing a preview of multi-cell paste data.
 * Supports both single-column (current → new) and 2D grid preview.
 * User confirms with "Apply" or cancels.
 */

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import type { PastePreview } from './types';
import type { DataGridLabels } from './dataGridLabels';

interface DataGridPasteDialogProps {
  preview: PastePreview | null;
  onApply: () => void;
  onCancel: () => void;
  isPasting: boolean;
  progress: { current: number; total: number } | null;
  labels?: Partial<DataGridLabels>;
}

const MAX_PREVIEW_ROWS = 5;

export function DataGridPasteDialog({
  preview,
  onApply,
  onCancel,
  isPasting,
  progress,
  labels,
}: DataGridPasteDialogProps) {
  if (!preview) return null;

  const lDescription = labels?.dgPasteDescription ?? 'Review the changes below before applying.';
  const lMore = labels?.dgPasteMore ?? 'more';
  const lSaving = labels?.dgPasteSaving ?? 'Saving';
  const lApply = labels?.dgPasteApply ?? 'Apply';
  const lSavingBtn = labels?.dgPasteSavingBtn ?? 'Saving...';
  const lCancel = labels?.dgCancel ?? 'Cancel';

  const is2D = preview.columns && preview.columns.length > 0 && preview.grid;

  // 2D grid preview
  if (is2D) {
    const { columns, grid } = preview as Required<Pick<PastePreview, 'columns' | 'grid'>>;
    const visibleRows = grid.slice(0, MAX_PREVIEW_ROWS);
    const remaining = grid.length - visibleRows.length;
    const title = `Paste ${grid.length}×${columns.length} cells`;

    return (
      <AlertDialog open onOpenChange={(open) => { if (!open && !isPasting) onCancel(); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{lDescription}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-56 overflow-auto rounded-xs border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  {columns.map((col) => (
                    <th
                      key={col.fieldname}
                      className="px-3 py-1.5 text-left font-medium text-muted-foreground"
                    >
                      {col.fieldLabel}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((gridRow, ri) => (
                  <tr key={ri} className="border-b last:border-0">
                    {gridRow.map((cell, ci) => (
                      <td key={ci} className="px-3 py-1.5">
                        <span className="text-muted-foreground text-xs">
                          {String(cell.currentValue ?? '—')}
                        </span>
                        <span className="text-muted-foreground mx-1">→</span>
                        <span className="font-medium">{cell.newValue || '—'}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {remaining > 0 && (
            <p className="text-xs text-muted-foreground">
              ...{remaining} {lMore}
            </p>
          )}

          {isPasting && progress && (
            <p className="text-sm text-muted-foreground">
              {lSaving} {progress.current} / {progress.total}...
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPasting} onClick={onCancel}>
              {lCancel}
            </AlertDialogCancel>
            <AlertDialogAction disabled={isPasting} onClick={onApply}>
              {isPasting ? lSavingBtn : lApply}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Single-column preview (original behavior)
  const { fieldLabel, rows } = preview;
  const visibleRows = rows.slice(0, MAX_PREVIEW_ROWS);
  const remaining = rows.length - visibleRows.length;
  const lPasteTitle = labels?.dgPasteTitle ?? 'Paste values';
  const lCurrent = labels?.dgPasteCurrent ?? 'Current';
  const lNew = labels?.dgPasteNew ?? 'New';

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open && !isPasting) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {lPasteTitle} — {rows.length} → &ldquo;{fieldLabel}&rdquo;
          </AlertDialogTitle>
          <AlertDialogDescription>
            {lDescription}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-48 overflow-auto rounded-xs border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">
                  {lCurrent}
                </th>
                <th className="px-1 py-1.5 text-center text-muted-foreground">→</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">
                  {lNew}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.name} className="border-b last:border-0">
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {String(row.currentValue ?? '—')}
                  </td>
                  <td className="px-1 py-1.5 text-center text-muted-foreground">→</td>
                  <td className="px-3 py-1.5 font-medium">
                    {row.newValue || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {remaining > 0 && (
          <p className="text-xs text-muted-foreground">
            ...{remaining} {lMore}
          </p>
        )}

        {isPasting && progress && (
          <p className="text-sm text-muted-foreground">
            {lSaving} {progress.current} / {progress.total}...
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPasting} onClick={onCancel}>
            {lCancel}
          </AlertDialogCancel>
          <AlertDialogAction disabled={isPasting} onClick={onApply}>
            {isPasting ? lSavingBtn : lApply}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
